
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import LinkTokenCreate from 'src/components/docs/LinkTokenCreate';
export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Learn how to migrate from Transactions to CRA Base Report'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Migrate from Transactions to Plaid Consumer Report' subtitle='Switch to CRA Base Report for FCRA-compliant reporting with enhanced data coverage' mdxType="Header" />
    <p>{`This guide will walk you through how to migrate from accessing bank data with Plaid's `}<a parentName="p" {...{
        "href": "https://plaid.com/products/transactions/"
      }}>{`Transactions`}</a>{` product to generating Base Reports with Plaid's `}<a parentName="p" {...{
        "href": "https://plaid.com/check/income-and-underwriting/"
      }}>{`Consumer Report`}</a>{` product.`}</p>
    <p>{`The CRA Base Report includes account balance, account ownership, and transaction data, and is designed for use cases that require FCRA-compliant consumer reporting, such as tenant screening, income verification, and credit decisioning.`}</p>
    <h4 {...{
      "id": "why-migrate"
    }}>{`Why migrate?`}</h4>
    <p>{`While the Transactions product gives you historical transaction data, the CRA Base Report enhances this by:`}</p>
    <ul>
      <li parentName="ul">{`Supporting FCRA-compliant usage  `}</li>
      <li parentName="ul">{`Bundling additional data (identity, balance, account metadata) in a single report  `}</li>
      <li parentName="ul">{`Managing the explicit consumer consent and disclosures required for underwriting use cases in the US `}</li>
    </ul>
    <p>{`If your use case involves evaluating the financial standing of US-based end users for underwriting, credit, or leasing, you should use the CRA Base Report instead of Transactions.`}</p>
    <h4 {...{
      "id": "prerequisites"
    }}>{`Prerequisites`}</h4>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`To use Consumer Report, it is strongly recommended to update your Plaid client library to the latest version. The minimum required versions for new Consumer Report integrations are:`}</p>
        <ul parentName="li">
          <li parentName="ul">{`Python: 38.0.0`}</li>
          <li parentName="ul">{`Go: 41.0.0`}</li>
          <li parentName="ul">{`Java: 39.0.0`}</li>
          <li parentName="ul">{`Node: 41.0.0`}</li>
          <li parentName="ul">{`Ruby: 45.0.0`}</li>
        </ul>
      </li>
      <li parentName="ol">
        <p parentName="li"><a parentName="p" {...{
            "href": "https://dashboard.plaid.com/settings/team/products"
          }}>{`Confirm`}</a>{` you have access to Plaid Check products in Production. If not, `}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/overview/request-products"
          }}>{`request access via the Dashboard`}</a>{`.`}</p>
      </li>
    </ol>
    <h4 {...{
      "id": "changes-to-plaid-link-initialization"
    }}>{`Changes to Plaid Link initialization`}</h4>
    <p>{`When using Plaid Check products, you must create a user before initializing Link. This allows Plaid to associate multiple Items with a single user.`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`Call `}<inlineCode parentName="p">{`/user/create`}</inlineCode>{` before `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`.`}</p>
        <ul parentName="li">
          <li parentName="ul">{`Include the `}<inlineCode parentName="li">{`identity`}</inlineCode>{` object. At minimum, the following fields must be provided and non-empty: `}<inlineCode parentName="li">{`name`}</inlineCode>{`, `}<inlineCode parentName="li">{`date_of_birth`}</inlineCode>{`, `}<inlineCode parentName="li">{`emails`}</inlineCode>{`, `}<inlineCode parentName="li">{`phone_numbers`}</inlineCode>{`, and `}<inlineCode parentName="li">{`addresses`}</inlineCode>{` (with at least one email address, phone number, and address designated as `}<inlineCode parentName="li">{`primary`}</inlineCode>{`). If you intend to share the report with a GSE (Government-Sponsored Enterprise) such as Fannie or Freddie, the full SSN is also required via the `}<inlineCode parentName="li">{`id_numbers`}</inlineCode>{` field. For all use cases, providing at least a partial SSN is highly recommended, since it improves the accuracy of matching user records during compliance processes such as file disclosure, dispute, or security freeze requests.`}</li>
          <li parentName="ul">{`Store the `}<inlineCode parentName="li">{`user_id`}</inlineCode>{` in your database.  `}</li>
        </ul>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Update your `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` call:  `}</p>
        <ul parentName="li">
          <li parentName="ul">{`Remove the `}<inlineCode parentName="li">{`transactions`}</inlineCode>{` object (if present) from the call.`}</li>
          <li parentName="ul">{`Include the `}<inlineCode parentName="li">{`user_id`}</inlineCode>{` from `}<inlineCode parentName="li">{`/user/create`}</inlineCode>{`.  `}</li>
          <li parentName="ul">{`Replace `}<inlineCode parentName="li">{`transactions`}</inlineCode>{` with `}<inlineCode parentName="li">{`cra_base_report`}</inlineCode>{` (and any other CRA products) in the `}<inlineCode parentName="li">{`products`}</inlineCode>{` array.  `}</li>
          <li parentName="ul">{`Add a `}<inlineCode parentName="li">{`cra_options`}</inlineCode>{` object with `}<inlineCode parentName="li">{`days_requested`}</inlineCode>{` (min 180).  `}</li>
          <li parentName="ul">{`Provide a `}<inlineCode parentName="li">{`consumer_report_permissible_purpose`}</inlineCode>{`.  `}</li>
          <li parentName="ul">{`(Optional) To allow multi-institution linking, set `}<inlineCode parentName="li">{`enable_multi_item_link`}</inlineCode>{` to `}<inlineCode parentName="li">{`true`}</inlineCode>{`.`}</li>
        </ul>
      </li>
    </ol>
    <LinkTokenCreate useCases={[{
      useCase: 'Consumer Report for credit and lending (Consumer Report by Plaid Check)',
      comment: "Generate the user_id by calling /user/create",
      config: {
        user: {
          client_user_id: 'user-abc',
          email_address: 'user@example.com'
        },
        user_id: 'usr_9nSp2KuZ2x4JDw',
        products: ['cra_base_report'],
        consumer_report_permissible_purpose: 'EXTENSION_OF_CREDIT',
        cra_options: {
          days_requested: 365
        },
        client_name: 'The Loan Arranger',
        language: 'en',
        country_codes: ['US'],
        webhook: 'https://sample-web-hook.com'
      }
    }]} filenameAsTitle={true} mdxType="LinkTokenCreate" />
    <h5 {...{
      "id": "adding-consumer-report-alongside-transactions-on-existing-items"
    }}>{`Adding Consumer Report alongside Transactions on existing Items`}</h5>
    <p>{`If you want an existing Transactions-enabled Item to also be enabled for Consumer Report (keeping both products active on the same Item), call `}<inlineCode parentName="p">{`/user/create`}</inlineCode>{` and `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` as described above, but include the Item's `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` when calling `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`. When Link is launched, the end user will go through the Consumer Report consent flow, and on successful completion of the flow, the Item will be enabled for Consumer Report in addition to Transactions.`}</p>
    <h4 {...{
      "id": "update-product-api-calls-and-webhook-listeners"
    }}>{`Update product API calls and webhook listeners`}</h4>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`Update your webhook listener endpoints, adding listeners for Plaid Check.`}</p>
        <ul parentName="li">
          <li parentName="ul">
            <p parentName="li">{`Upon receiving the `}<a parentName="p" {...{
                "href": "/docs/api/products/check-legacy/#user_check_report_ready"
              }}><inlineCode parentName="a">{`USER_CHECK_REPORT_READY`}</inlineCode></a>{` webhook, call product endpoints such as `}<inlineCode parentName="p">{`/cra/check_report/base_report/get`}</inlineCode>{` or (if using) `}<inlineCode parentName="p">{`/cra/check_report/partner_insights/get`}</inlineCode>{`.`}</p>
          </li>
          <li parentName="ul">
            <p parentName="li">{`Upon receiving the `}<a parentName="p" {...{
                "href": "/docs/api/products/check-legacy/#user_check_report_failed"
              }}><inlineCode parentName="a">{`USER_CHECK_REPORT_FAILED`}</inlineCode></a>{` webhook, call `}<inlineCode parentName="p">{`/user/items/get`}</inlineCode>{` to determine why Items are in a bad state. If appropriate, send the user through `}<a parentName="p" {...{
                "href": "/docs/link/update-mode"
              }}>{`update mode`}</a>{` to repair the Item.`}</p>
          </li>
        </ul>
      </li>
      <li parentName="ol">
        <p parentName="li">{`If you're still using other Plaid products like `}<a parentName="p" {...{
            "href": "https://plaid.com/products/auth/"
          }}>{`Auth`}</a>{` or `}<a parentName="p" {...{
            "href": "https://plaid.com/products/balance/"
          }}>{`Balance`}</a>{` on the same Item, continue exchanging the public token for an access token after Link.`}</p>
      </li>
    </ol>
    <h4 {...{
      "id": "mapping-api-responses"
    }}>{`Mapping API responses`}</h4>
    <p>{`This `}<a parentName="p" {...{
        "href": "[redacted]##########################################################################################################"
      }}>{`Google Sheet`}</a>{` highlights the correspondences between Transactions and CRA Base Report schemas.`}</p>
    <p>{`If you are using other Plaid Inc. products, note that the `}<inlineCode parentName="p">{`account_id`}</inlineCode>{` returned in API responses from endpoints prefixed with `}<inlineCode parentName="p">{`/cra/`}</inlineCode>{` will not match the `}<inlineCode parentName="p">{`account_id`}</inlineCode>{` returned in responses from non-CRA Plaid endpoints.`}</p>
    <p>{`Unlike Transactions, a Base Report may return transactions whose details are masked to comply with the FCRA. On a masked transaction, `}<inlineCode parentName="p">{`original_description`}</inlineCode>{`, `}<inlineCode parentName="p">{`merchant_name`}</inlineCode>{`, and `}<inlineCode parentName="p">{`location.address`}</inlineCode>{` are returned as `}<inlineCode parentName="p">{`XXXX`}</inlineCode>{`; `}<inlineCode parentName="p">{`location.lat`}</inlineCode>{` and `}<inlineCode parentName="p">{`location.lon`}</inlineCode>{` are `}<inlineCode parentName="p">{`null`}</inlineCode>{`; and `}<inlineCode parentName="p">{`credit_category`}</inlineCode>{` has a `}<inlineCode parentName="p">{`primary`}</inlineCode>{` of `}<inlineCode parentName="p">{`MASKED`}</inlineCode>{` and a `}<inlineCode parentName="p">{`detailed`}</inlineCode>{` of `}<inlineCode parentName="p">{`MASKED_TRANSACTION_CATEGORY`}</inlineCode>{`. The `}<inlineCode parentName="p">{`amount`}</inlineCode>{`, `}<inlineCode parentName="p">{`date`}</inlineCode>{`, and account fields are unaffected, so masked transactions still contribute to cash flow calculations, but they can't be categorized or attributed to a merchant.`}</p>
    <h4 {...{
      "id": "migrating-existing-items-fully-off-transactions"
    }}>{`Migrating existing Items fully off Transactions`}</h4>
    <p>{`If you want to replace Transactions with Plaid Check on an existing Item (rather than running both alongside, as described above), there is no way to do this directly. Instead, you must delete the old Item using `}<inlineCode parentName="p">{`/item/remove`}</inlineCode>{` and prompt the user to go through the Link flow again, using a Link token that is enabled for Plaid Check. `}</p>
    <p>{`If you are ready to migrate all Items, use `}<inlineCode parentName="p">{`/item/remove`}</inlineCode>{` to delete the Transactions-enabled Items, and prompt your users to return to your app to re-add their accounts using the new Plaid Check-enabled flow. You can also perform this process in stages, disabling only a percentage of Items at a time. `}</p>
    <p>{`The most conservative gradual migration strategy is to not delete the Transactions-enabled Item until it is in an unhealthy state that requires user interaction to fix (e.g. `}<inlineCode parentName="p">{`ITEM_LOGIN_REQUIRED`}</inlineCode>{`). At that time, instead of sending the Item through update mode, delete the Item using `}<inlineCode parentName="p">{`/item/remove`}</inlineCode>{` and prompt your users to re-add their accounts using the new Plaid Check-enabled flow.`}</p>
    <h4 {...{
      "id": "removing-transactions-logic-for-existing-items"
    }}>{`Removing Transactions logic for existing Items`}</h4>
    <p>{`Depending on your use case and business, you may want to leave existing Transactions-enabled Items in place and perform a gradual migration, as described above. Do not remove Transactions logic unless you are no longer using any Transactions-enabled Items.`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`Remove handling for Transactions webhooks, including `}<inlineCode parentName="p">{`SYNC_UPDATES_AVAILABLE`}</inlineCode>{`, `}<inlineCode parentName="p">{`INITIAL_UPDATE`}</inlineCode>{`, `}<inlineCode parentName="p">{`HISTORICAL_UPDATE`}</inlineCode>{`, `}<inlineCode parentName="p">{`DEFAULT_UPDATE`}</inlineCode>{`, `}<inlineCode parentName="p">{`TRANSACTIONS_REMOVED`}</inlineCode>{`, and `}<inlineCode parentName="p">{`RECURRING_TRANSACTIONS_UPDATE`}</inlineCode>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Remove calls to `}<inlineCode parentName="p">{`/transactions/`}</inlineCode>{` endpoints such as `}<inlineCode parentName="p">{`/transactions/get`}</inlineCode>{`, `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{`, or `}<inlineCode parentName="p">{`/transactions/recurring/get`}</inlineCode>{`, as well as any handling for transactions-specific logic, such as transactions updates and pagination.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Remove handling for the `}<inlineCode parentName="p">{`TRANSACTIONS_SYNC_MUTATION_DURING_PAGINATION`}</inlineCode>{` error.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`If you have existing Transactions-enabled Items you will no longer be using, call `}<inlineCode parentName="p">{`/item/remove`}</inlineCode>{` to delete these Items so you will no longer be billed for Transactions.`}</p>
      </li>
    </ol>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "why-migrate",
        level: 4,
        title: "Why migrate?",
      },
{
        id: "prerequisites",
        level: 4,
        title: "Prerequisites",
      },
{
        id: "changes-to-plaid-link-initialization",
        level: 4,
        title: "Changes to Plaid Link initialization",
      },
{
        id: "update-product-api-calls-and-webhook-listeners",
        level: 4,
        title: "Update product API calls and webhook listeners",
      },
{
        id: "mapping-api-responses",
        level: 4,
        title: "Mapping API responses",
      },
{
        id: "migrating-existing-items-fully-off-transactions",
        level: 4,
        title: "Migrating existing Items fully off Transactions",
      },
{
        id: "removing-transactions-logic-for-existing-items",
        level: 4,
        title: "Removing Transactions logic for existing Items",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
